import React, { useEffect, useMemo, useState } from 'react';
import {
  Background,
  Connection,
  Controls,
  MiniMap,
  ReactFlow,
  useNodesState,
  NodeChange,
  applyNodeChanges,
} from 'reactflow';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../support/store';
import { useActiveJourneyMapDraft } from '../../../../hooks/journey-map-hooks';
import {
  addJourneyMapDraftLinkBetweenItems,
  recalculateDraftOrderFromLayout,
  selectJourneyMapDraftItem,
  selectJourneyMapDraftLink,
  updateJourneyMapDraftItemPosition,
  updateJourneyMapDraftLink,
} from '../../../../support/slices/journeyMapBuilderSlice';
import JourneyMapCanvasNode from './JourneyMapCanvasNode';
import JourneyMapCanvasStartNode from './JourneyMapCanvasStartNode';
import JourneyMapCanvasViewportSync from './JourneyMapCanvasViewportSync';
import JourneyMapCanvasOverlay from './JourneyMapCanvasOverlay';
import {
  buildCanvasEdges,
  buildCanvasNodes,
  loadReferenceLabelMap,
  normalizeDraftLinksWithTempIds,
  resolveNearestHandlesForItems,
} from './journeyMapCanvasUtils';
import {
  CANVAS_DEFAULT_HEIGHT,
  CANVAS_DEFAULT_WIDTH,
  MAX_ZOOM,
  MIN_ZOOM,
  PROVIDER_OPTIONS,
} from './journeyMapCanvasConstants';
import { SectionDelimiterRecord } from './journeyMapCanvasTypes';
import JourneyMapCanvasEdge from './JourneyMapCanvasEdge';

type Props = {
  backgroundImageUrl?: string | null;
  sectionDelimiters?: SectionDelimiterRecord[];
  canvasWidth?: number;
  canvasHeight?: number;
};

const nodeTypes = {
  journeyItem: JourneyMapCanvasNode,
  startPlaceholder: JourneyMapCanvasStartNode,
};

const edgeTypes = {
  journeyMapCustom: JourneyMapCanvasEdge,
};

export default function JourneyMapCanvasInner({
                                                backgroundImageUrl = null,
                                                sectionDelimiters = [],
                                                canvasWidth = CANVAS_DEFAULT_WIDTH,
                                                canvasHeight = CANVAS_DEFAULT_HEIGHT,
                                              }: Props) {
  const dispatch = useAppDispatch();
  const draft = useActiveJourneyMapDraft();
  const [referenceLabelMap, setReferenceLabelMap] = useState<Record<string, string>>({});

  const selectedItemTempId = useSelector((state: RootState) => {
    const activeDraftId = state.journeyMapBuilder.activeDraftId;
    const activeDraft = state.journeyMapBuilder.drafts.find((d) => d.tempId === activeDraftId);
    return activeDraft?.selectedItemTempId || null;
  });

  const selectedLinkTempId = useSelector((state: RootState) => {
    const activeDraftId = state.journeyMapBuilder.activeDraftId;
    const activeDraft = state.journeyMapBuilder.drafts.find((d) => d.tempId === activeDraftId);
    return activeDraft?.selectedLinkTempId || null;
  });

  useEffect(() => {
    let active = true;

    (async () => {
      const map = await loadReferenceLabelMap(draft, PROVIDER_OPTIONS);
      if (active) setReferenceLabelMap(map);
    })();

    return () => {
      active = false;
    };
  }, [draft]);

  const visibleItems = useMemo(() => {
    return draft?.items.filter((item) => !item.isDeleted) || [];
  }, [draft]);

  const visibleLinks = useMemo(() => {
    const normalizedLinks = normalizeDraftLinksWithTempIds(draft);
    return normalizedLinks.filter((link: any) => !link.isDeleted);
  }, [draft]);

  const computedNodes = useMemo(() => {
    return buildCanvasNodes(
      draft,
      visibleItems,
      selectedItemTempId,
      referenceLabelMap,
      canvasWidth,
      canvasHeight,
    );
  }, [draft, visibleItems, selectedItemTempId, referenceLabelMap, canvasWidth, canvasHeight]);

  const [nodes, setNodes, onNodesChangeBase] = useNodesState(computedNodes);

  useEffect(() => {
    setNodes((currentNodes) => {
      const currentIds = currentNodes.map((node) => String(node.id)).sort().join('|');
      const nextIds = computedNodes.map((node) => String(node.id)).sort().join('|');

      if (currentIds !== nextIds) {
        return computedNodes;
      }

      return computedNodes.map((nextNode) => {
        const existing = currentNodes.find((node) => String(node.id) === String(nextNode.id));
        if (!existing) return nextNode;

        return {
          ...nextNode,
          position: existing.position,
        };
      });
    });
  }, [computedNodes, setNodes]);

  const onNodesChange = (changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  };

  const edges = useMemo(() => {
    return buildCanvasEdges(draft, visibleLinks as any, selectedLinkTempId);
  }, [draft, visibleLinks, selectedLinkTempId]);

  const handleConnect = (connection: Connection) => {
    const source = connection.source;
    const target = connection.target;
    if (!source || !target) return;

    const sourceItem = visibleItems.find((item) => String(item.tempId) === String(source));
    const targetItem = visibleItems.find((item) => String(item.tempId) === String(target));

    const nearestHandles =
      sourceItem && targetItem
        ? resolveNearestHandlesForItems({
          sourceItem,
          targetItem,
        })
        : null;

    dispatch(
      addJourneyMapDraftLinkBetweenItems({
        fromItemTempId: String(source),
        toItemTempId: String(target),
        linkType: 'default',
        sourceHandle: connection.sourceHandle || nearestHandles?.sourceHandle || null,
        targetHandle: connection.targetHandle || nearestHandles?.targetHandle || null,
      }),
    );
  };

  const handleEdgeUpdate = (oldEdge: any, newConnection: Connection) => {
    const source = newConnection.source;
    const target = newConnection.target;
    if (!source || !target) return;

    const sourceItem = visibleItems.find((item) => String(item.tempId) === String(source));
    const targetItem = visibleItems.find((item) => String(item.tempId) === String(target));

    const nearestHandles =
      sourceItem && targetItem
        ? resolveNearestHandlesForItems({
          sourceItem,
          targetItem,
        })
        : null;

    dispatch(
      updateJourneyMapDraftLink({
        tempId: String(oldEdge.id),
        changes: {
          from_item_temp_id: String(source),
          to_item_temp_id: String(target),
          source_handle: newConnection.sourceHandle || nearestHandles?.sourceHandle || null,
          target_handle: newConnection.targetHandle || nearestHandles?.targetHandle || null,
        },
      }),
    );
  };

  if (!draft) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 520,
          border: '1px solid #E1E4E8',
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        No active draft selected.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 520,
        border: '1px solid #E1E4E8',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <JourneyMapCanvasOverlay
        backgroundImageUrl={backgroundImageUrl}
        sectionDelimiters={sectionDelimiters}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          width: '100%',
          height: '100%',
          minHeight: 520,
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          nodesDraggable={visibleItems.length > 0}
          onNodesChange={onNodesChange}
          onConnect={handleConnect}
          onEdgeUpdate={handleEdgeUpdate}
          onNodeClick={(_, node) => {
            dispatch(selectJourneyMapDraftItem(String(node.id)));
            dispatch(selectJourneyMapDraftLink(null));
          }}
          onPaneClick={() => {
            dispatch(selectJourneyMapDraftItem(null));
            dispatch(selectJourneyMapDraftLink(null));
          }}
          onNodeDragStop={(_, node) => {
            dispatch(selectJourneyMapDraftItem(String(node.id)));
            dispatch(selectJourneyMapDraftLink(null));
            dispatch(
              updateJourneyMapDraftItemPosition({
                tempId: String(node.id),
                position_x: node.position.x,
                position_y: node.position.y,
              }),
            );
            dispatch(recalculateDraftOrderFromLayout());
          }}
          onEdgeClick={(_, edge) => {
            dispatch(selectJourneyMapDraftLink(String(edge.id)));
            dispatch(selectJourneyMapDraftItem(null));
          }}
          fitViewOptions={{
            padding: 0.12,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
          }}
        >
          <JourneyMapCanvasViewportSync nodes={nodes} />
          <MiniMap
            nodeColor={() => '#2774D9'}
            maskColor="rgba(35, 40, 46, 0.08)"
          />
          <Controls />
          <Background gap={20} size={1} color="#E1E4E8" />
        </ReactFlow>
      </Box>
    </Box>
  );
}